import * as comLink from 'comlink'
import type { ChannelDetail, IDevice, IBaseDevice } from './IDevice'
import { Builder as DeviceBuilder } from '@core/devices/Device'
import type { DeviceConnectionCheckWorker } from '@/workers/DeviceConnectionCheckWorker'
import DeviceConnectionCheckWorkerConstructor from '../../workers/DeviceConnectionCheckWorker?worker&inline'

class DeviceConnectionChecker {
  // TODO: find comLink type for deviceWorker
  #deviceChecker: comLink.Remote<DeviceConnectionCheckWorker> | undefined = undefined
  #deviceMap = new Map<string, IDevice>()
  #deviceConnectionStateUpdateListener?: (device: IDevice, channel: ChannelDetail) => void

  setListener(listener: (device: IDevice, channel: ChannelDetail) => void) {
    this.#deviceConnectionStateUpdateListener = listener
  }

  async #changeDeviceStatus(device: IBaseDevice): Promise<void> {
    const currentDevice = this.#deviceMap.get(device.id)

    if (currentDevice && device.channel) {
      for (const channel of device.channel) {
        this.#updateDeviceChannel(currentDevice, channel)
      }
    }
  }

  async #updateDeviceChannel(currentDevice: IDevice, channel: ChannelDetail) {
    const currentChannelHasChanged = currentDevice.updateChannelAvailability(channel)

    if (currentChannelHasChanged) {
      this.#deviceMap.set(currentDevice.id, currentDevice)
      this.#deviceConnectionStateUpdateListener?.(currentDevice, channel)
    }
  }

  async start(deviceList: IDevice[]) {
    if (!this.#deviceChecker) {
      this.#deviceChecker = comLink.wrap<DeviceConnectionCheckWorker>(new DeviceConnectionCheckWorkerConstructor())
    }

    if (this.#deviceChecker) {
      this.#deviceChecker.clear()

      this.#deviceMap = new Map<string, IDevice>()

      const workerDeviceList: IBaseDevice[] = []

      for (const device of deviceList) {
        const buildDevice = DeviceBuilder.buildFromDevice(device)

        this.#deviceMap.set(device.id, buildDevice)

        const channel: ChannelDetail[] | undefined = device.getAllChannelInfo()

        if (!channel || channel.length === 0) continue

        const { accessToken } = device

        workerDeviceList.push({ id: device.id, model: device.model, channel, accessToken, version: device.version })
      }

      this.#deviceChecker.polling(
        workerDeviceList,
        comLink.proxy((value: IBaseDevice) => this.#changeDeviceStatus(value))
      )
    }
  }

  stop() {
    this.#deviceChecker?.clear()
    this.#deviceChecker = undefined
  }
}

const INSTANCE = new DeviceConnectionChecker()

export { INSTANCE }
